import React, { useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Provider, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import store from '../store';
import { deviceCategories } from '../common/deviceCategories';
import StatusView from '../map/StatusView';
import StartsStopsView from "../map/StartsStopsView";
import {ThemeProvider} from "@material-ui/core/styles";
import {useTheme} from "@material-ui/core";

const getStatusColor = (status) => {
    switch (status) {
        case 'online':
            return 'green';
        case 'offline':
            return 'gray';
        case 'slowing':
            return 'yellow';
        default:
            return 'red';
    }
};

const createRotatedIcon = (baseDataUrl, rotation, size) => {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext('2d');
            ctx.save();
            ctx.translate(size / 2, size / 2);
            ctx.rotate(rotation * Math.PI / 180);
            ctx.drawImage(img, -size / 2, -size / 2, size, size);
            ctx.restore();
            resolve(canvas.toDataURL());
        };
        img.src = baseDataUrl;
    });
};

const PositionsMapGoogle = ({ map, googleMapsApi, icons, positions, replayBack }) => {
    const markersRef = useRef({});
    const theme = useTheme();
    const infoWindowRef = useRef(null);
    const history = useHistory();
    const devices = useSelector((state) => state.devices.items);
    const currentPos  = useRef(null)
    const mapType = useSelector(state => state.session.user && state.session.user.map)

    const lastCourseRef = useRef({});
    const prevCoordsRef = useRef({});

    const bearing = (lat1, lon1, lat2, lon2) => {
        const toRad = deg => deg * Math.PI / 180;
        const toDeg = rad => rad * 180 / Math.PI;
        const dLon = toRad(lon2 - lon1);
        const y = Math.sin(dLon) * Math.cos(toRad(lat2));
        const x = Math.cos(toRad(lat1)) * Math.sin(toRad(lat2))
            - Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLon);
        return (toDeg(Math.atan2(y, x)) + 360) % 360;
    };

    const handleMarkerClick = useCallback((marker, deviceId, device) => {
        if (!map || !googleMapsApi || !infoWindowRef.current) return;
        window.dispatchEvent(new CustomEvent('close-devices-list-info-window'));

        const placeholder = document.createElement('div');
        ReactDOM.render(
                <Provider store={store}>
                    {!replayBack ?
                        <ThemeProvider theme={theme}>
                            <StatusView
                                mapGoogle={map}
                                deviceId={deviceId}
                                onShowDetails={(positionId) => history.push(`/position/${positionId}`)}
                                history={history}
                            />
                        </ThemeProvider>
                        :
                        <ThemeProvider theme={theme}>
                            <StartsStopsView position={currentPos.current} status={"positionFixTime"} deviceName={device.name} mapType={mapType}/>
                        </ThemeProvider>
                    }
                </Provider>,
            placeholder
        );

        infoWindowRef.current.setContent(placeholder);
        infoWindowRef.current.open(map, marker);

        googleMapsApi.event.addListenerOnce(infoWindowRef.current, 'closeclick', () => {
            ReactDOM.unmountComponentAtNode(placeholder);
        });
    }, [history, theme]);

    useEffect(() => {
        let mapClickListener;
        if (map && googleMapsApi) {
            if (!infoWindowRef.current) {
                infoWindowRef.current = new googleMapsApi.InfoWindow();
            }
            mapClickListener = map.addListener('click', () => {
                if (infoWindowRef.current) {
                    const currentContent = infoWindowRef.current.getContent();
                    if (currentContent instanceof HTMLElement) ReactDOM.unmountComponentAtNode(currentContent);
                    infoWindowRef.current.close();
                }
            });
            const onCloseMapInfoWindow = () => {
                if (infoWindowRef.current) {
                    const currentContent = infoWindowRef.current.getContent();
                    if (currentContent instanceof HTMLElement) ReactDOM.unmountComponentAtNode(currentContent);
                    infoWindowRef.current.close();
                }
            };
            window.addEventListener('close-map-info-window', onCloseMapInfoWindow);
            return () => {
                if (mapClickListener) {
                    googleMapsApi.event.removeListener(mapClickListener);
                }
                if (infoWindowRef.current) {
                    const currentContent = infoWindowRef.current.getContent();
                    if (currentContent instanceof HTMLElement) ReactDOM.unmountComponentAtNode(currentContent);
                    infoWindowRef.current.close();
                }
                window.removeEventListener('close-map-info-window', onCloseMapInfoWindow);
            };
        }
    }, [map, googleMapsApi, theme]);

    const fitMapToMarkers = useCallback(() => {
        const allMarkers = Object.values(markersRef.current);
        if (allMarkers.length > 0 && map && map.fitBounds) {
            const bounds = new googleMapsApi.LatLngBounds();
            allMarkers.forEach((marker) => bounds.extend(marker.getPosition()));
            map.fitBounds(bounds);
            googleMapsApi.event.addListenerOnce(map, 'idle', () => {
                if (map.getZoom() > 12) {
                    map.setZoom(12);
                }
            });
        }
    }, [map, googleMapsApi, theme]);

    useEffect(() => {
        if (!map || !googleMapsApi || !icons || Object.keys(icons).length === 0 || !positions) {
            return;
        }

        const newMarkers = [];

        positions.forEach((position) => {
            const device = devices[position.deviceId];
            if (!device) return;
            currentPos.current = position;

            const deviceId = position.deviceId;
            const deviceName = device.name || `Device ID: ${deviceId}`;
            const statusColor = getStatusColor(device.status);
            const category = deviceCategories.includes(device.category) ? device.category : 'default';
            const baseIcon = icons[`${category}Position-${statusColor}`];
            const prev = prevCoordsRef.current[deviceId];
            // The 'default' icon is a map pin: its tip must keep pointing at the
            // coordinate, so it never rotates. Every other category is a directional
            // top-down vehicle sprite. Reuses the `category` resolved above (not
            // device.category) so the rotation guard and the icon can never
            // disagree — a missing category arrives as either null or undefined
            // depending on the payload.
            const rotates = category !== 'default';
            let course;
            if (!rotates) {
                course = 0;
            } else if (prev && (prev.lat !== position.latitude || prev.lon !== position.longitude)) {
                course = bearing(prev.lat, prev.lon, position.latitude, position.longitude);
            } else if (!prev && (typeof position.course === 'number' || typeof position.attributes?.course === 'number')) {
                // First render for this device — its last known position from the initial
                // load / socket snapshot: initialise the heading from the reported course
                // instead of defaulting to north. The movement bearing above still wins on
                // subsequent updates, so real-time rotation is unchanged.
                course = typeof position.course === 'number' ? position.course : position.attributes.course;
            } else if (position.attributes && typeof position.attributes.course === 'number') {
                course = position.attributes.course;
            } else {
                course = lastCourseRef.current[deviceId] || 0;
            }
            lastCourseRef.current[deviceId] = course;
            prevCoordsRef.current[deviceId] = { lat: position.latitude, lon: position.longitude };

            if (!baseIcon) console.warn('[Tracthing][Google] Icon not found for', deviceName);

            if (typeof position.latitude !== 'number' || typeof position.longitude !== 'number') {
                console.warn(`Invalid coordinates for device: ${deviceName}`, position);
                return;
            }

            const positionLatLon = new googleMapsApi.LatLng(position.latitude, position.longitude);

            const updateMarkerIcon = async (marker) => {
                if (course !== 0 && baseIcon && baseIcon.url) {
                    const rotatedUrl = await createRotatedIcon(baseIcon.url, course, 64);
                    marker.setIcon({
                        url: rotatedUrl,
                        scaledSize: new googleMapsApi.Size(48, 48),
                        anchor: new googleMapsApi.Point(24, 24),
                    });
                } else if (baseIcon) {
                    marker.setIcon(baseIcon);
                }
            };

            if (markersRef.current[deviceId]) {
                const marker = markersRef.current[deviceId];
                marker.setPosition(positionLatLon);
                updateMarkerIcon(marker);
            } else {
                const marker = new googleMapsApi.Marker({
                    position: positionLatLon,
                    map,
                    icon: baseIcon,
                    title: deviceName,
                    deviceId: deviceId,
                });
                marker.addListener('click', () => handleMarkerClick(marker, deviceId, device));
                markersRef.current[deviceId] = marker;
                newMarkers.push(marker);
                if (course !== 0 && baseIcon && baseIcon.url) {
                    updateMarkerIcon(marker);
                }
            }
        });

        if (newMarkers.length > 0 && !replayBack) fitMapToMarkers();
    }, [map, googleMapsApi, icons, positions, devices, handleMarkerClick, theme]);

    useEffect(() => () => {
        Object.values(markersRef.current).forEach((marker) => {
            googleMapsApi.event.clearInstanceListeners(marker);
            marker.setMap(null);
        });
        markersRef.current = {};
        if (infoWindowRef.current) {
            infoWindowRef.current.close();
        }
    }, [theme]);

    useEffect(() => {
        const styleId = 'google-maps-cyber-popup';
        let styleElement = document.getElementById(styleId);

        if (!styleElement) {
            styleElement = document.createElement('style');
            styleElement.id = styleId;
            document.head.appendChild(styleElement);
        }

        const isDarkMode = theme.palette.typeBackground === 'dark';
        const popupBg = isDarkMode ? 'rgba(15, 25, 35, 0.7)' : 'rgba(255, 255, 255, 0.5)';
        const glowOpacity = isDarkMode ? '0.4' : '0.2';

        styleElement.innerHTML = `
    /* Main wrapper container override */
    .gm-style-iw-c {
      background: ${popupBg} !important;
      backdrop-filter: blur(10px) !important;
      -webkit-backdrop-filter: blur(10px) !important;
      border: 1px solid #00f2ff !important;
      box-shadow: 0 0 20px rgba(0, 242, 255, ${glowOpacity}) !important;
      border-radius: 20px !important;
      padding: 0 !important;
    }

    /* Target inner content block container */
    .gm-style-iw-d {
      overflow: hidden !important;
      padding: 0 !important;
      max-height: none !important;
    }

    /* Google Maps native close button container */
    .gm-style-iw-chr {
      position: absolute !important;
      right: 0;
      top: 0;
      z-index: 1;
    }

    /* Close Button Wrapper */
    .gm-style-iw-chr > button.gm-ui-hover-effect {
     flex: 0 0 50% !important; /* Combines grow, shrink, and basis */
     height: 15px !important;
      margin: 4px 4px 0 0 !important;
     padding: 4px !important;
     outline: none !important;
     color: #00f2ff !important;
     opacity: 0.8 !important;
     transition: background-color 0.2s ease;
    }
   /* Close Button Hover */
   .gm-style-iw-chr > button.gm-ui-hover-effect:hover {
     background-color: rgba(0, 242, 255, 0.1) !important;
     border-radius: 50% !important;
     opacity: 1 !important;
    }
    
    /* Inner 'X' Icon Graphic */
    .gm-style-iw-chr > button.gm-ui-hover-effect > span {
     width: 100% !important;
    height: 100% !important;
     margin: 0 !important;
     background-color: #00f2ff !important;
     mask-repeat: no-repeat !important;
     mask-position: right !important; 
    }

    /* Target the popup tail/arrow pointer */
    .gm-style-iw-tc::after {
      background: ${popupBg} !important;
      backdrop-filter: blur(10px) !important;
      -webkit-backdrop-filter: blur(10px) !important;
      border: 1px solid #00f2ff !important;
      /* Mask border rules so it seamlessly joins the box border */
      border-top: none !important;
      border-left: none !important;
      box-shadow: 7px 7px 15px rgba(0, 242, 255, ${glowOpacity}) !important;
    }
  `;

        return () => {
            // Optional: if (styleElement) styleElement.remove();
        };
    }, [theme]);

    return null;
};

export default PositionsMapGoogle;